/**
 * Error handling utilities
 * Properly extracts error messages from API responses, including Pydantic validation errors
 */

/**
 * Extract a human-readable error message from an API error response
 * Handles Pydantic v2 validation errors which come as arrays of objects
 * 
 * @param {Error} error - The caught error object
 * @param {string} fallbackMessage - Default message if no detail can be extracted
 * @returns {string} Human-readable error message
 */
export const getErrorMessage = (error, fallbackMessage = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail) && detail.length > 0) {
    const firstError = detail[0];
    
    // Pydantic v2 format: { type, loc, msg, input, ctx, url }
    if (firstError?.msg) {
      // Include the field location if available
      const field = firstError.loc?.slice(-1)[0];
      if (field && typeof field === 'string') {
        return `${field}: ${firstError.msg}`;
      }
      return firstError.msg;
    }
    
    // Fallback for other array formats
    if (firstError?.message) {
      return firstError.message;
    }
    
    // Last resort - stringify if it's an object
    if (typeof firstError === 'object') {
      return JSON.stringify(firstError);
    }
    
    return String(firstError);
  }
  
  // If detail is an object with a message property
  if (detail && typeof detail === 'object' && detail.message) {
    return detail.message;
  }
  
  // Check for standard error message
  if (error?.message) {
    return error.message;
  }
  
  return fallbackMessage;
};

export default getErrorMessage;
